import { useCallback, useEffect, useState } from "react";

// localStorage-backed recently-used tracker with frecency scoring.
// Schema-versioned so future changes don't break.
const KEY = "tv.recent.v1";
const MAX_ENTRIES = 20;

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter(
      (x) => x && typeof x.id === "string" && typeof x.ts === "number",
    );
  } catch {
    return [];
  }
}

function write(entries) {
  try {
    localStorage.setItem(KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch {
    /* localStorage full or disabled — ignore. */
  }
}

/**
 * Frecency-tracked recently-used items.
 * `bump(id)` records a use; `top(n)` returns the top-n ids sorted by score.
 *
 * Score = log(visits + 1) - decay * (now - ts_ms) / 86_400_000
 * (visits weighted log so a single recent open doesn't permanently beat
 * a frequently-used tool; decay = 0.05 per day so freshness still matters.)
 */
export default function useRecent() {
  const [entries, setEntries] = useState(() => read());

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === KEY) setEntries(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const bump = useCallback((id) => {
    if (!id) return;
    const now = Date.now();
    const next = (() => {
      const existing = entries.find((e) => e.id === id);
      if (existing) {
        return entries.map((e) =>
          e.id === id ? { ...e, ts: now, visits: (e.visits || 1) + 1 } : e,
        );
      }
      return [{ id, ts: now, visits: 1 }, ...entries];
    })();
    setEntries(next);
    write(next);
  }, [entries]);

  const top = useCallback(
    (n = 5) => {
      const now = Date.now();
      const scored = entries.map((e) => ({
        id: e.id,
        score:
          Math.log((e.visits || 1) + 1) -
          0.05 * ((now - e.ts) / 86_400_000),
      }));
      scored.sort((a, b) => b.score - a.score);
      return scored.slice(0, n).map((x) => x.id);
    },
    [entries],
  );

  return { entries, bump, top };
}
